import { ButtonWithLoading, ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { MenuItem, TextField } from "@mui/material";
import Button from "@mui/material/Button";
import Grid2 from "@mui/material/Grid2";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/lab/Alert";
import { type SyntheticEvent, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import type { Section } from "../../../models/Section";
import { useFormsContext } from "../../../context/FormsContext";
import { INITIAL_VALUES_SECTION } from "../../../models/SectionInitialValues";
import { upsertSection } from "../../../repositories/FormsRepository";
import { ColorPicker } from "./color-picker";

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	data: Section | null;
	newMode?: boolean;
};

export const NewAndEditSectionModal = ({
	show,
	onHide,
	data,
	newMode = false,
}: Props) => {
	const {
		state: { FormId, SectionId },
		actions: {
			setSectionNameSelectedInTable,
			setSectionBackgroundColorSelectedInTable,
			setSectionSubtitleSelectedInTable,
			setSectionLayoutParametersSelectedInTable,
		},
	} = useFormsContext();
	const [editedSuccess, setEditedSuccess] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);

	const [error, setError] = useState<string>("");
	const [deleteByIdError, setDeleteByIdError] = useState<string>("");
	const {
		control,
		handleSubmit,
		watch,
		setValue,
		formState: { errors },
		reset,
	} = useForm<Section>({ defaultValues: INITIAL_VALUES_SECTION });

	const handleClose = (
		event: Event | React.SyntheticEvent,
		reason?: string,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setEditedSuccess(false);
	};
	const handleCancel = async () => {
		reset();
		onHide(false);
	};

	const sectionName = watch("SectionName");

	const changeSectionName = () => {
		setSectionNameSelectedInTable(sectionName);
	};

	const subtitle = watch("Subtitle");

	const changeSubtitle = () => {
		setSectionSubtitleSelectedInTable(subtitle);
	};

	const layoutParameters = watch("LayoutParameters");

	const changeLayoutParameters = () => {
		setSectionSubtitleSelectedInTable(layoutParameters);
	};

	const backgroundColor = watch("BackgroundColor");

	const changeBackgroundColor = () => {
		setSectionBackgroundColorSelectedInTable(backgroundColor);
	};

	const onSubmit = async (sectionData: Section) => {
		setIsSubmitLoading(true);
		const res = await upsertSection(
			FormId,
			SectionId,
			sectionData?.SectionName,
			sectionData?.BackgroundColor as string,
			sectionData?.Subtitle as string,
			sectionData?.LayoutParameters as string,
		);
		if (res.ok) {
			reset();
			setIsSubmitLoading(false);
			setEditedSuccess(true);
			onHide(true);
		} else {
			setIsSubmitLoading(false);
			setError(res.message);
		}
	};

	useEffect(() => {
		if (data != null && show) {
			reset(INITIAL_VALUES_SECTION);
			setSectionNameSelectedInTable(data.SectionName);
			setSectionBackgroundColorSelectedInTable(data.BackgroundColor);
			setSectionSubtitleSelectedInTable(data.Subtitle);
			setSectionLayoutParametersSelectedInTable(data.LayoutParameters);
			setValue("SectionName", data.SectionName);
			setValue("BackgroundColor", data.BackgroundColor);
			setValue("Subtitle", data.Subtitle);
			setValue("LayoutParameters", data.LayoutParameters);
			setIsSubmitLoading(false);
		} else {
			reset(INITIAL_VALUES_SECTION);
		}
	}, [show]);

	useEffect(() => {
		changeSectionName();
	}, [sectionName]);

	useEffect(() => {
		changeBackgroundColor();
	}, [backgroundColor]);

	useEffect(() => {
		changeSubtitle();
	}, [subtitle]);

	useEffect(() => {
		changeLayoutParameters();
	}, [layoutParameters]);

	return (
		<>
			<MesfModal
				title={newMode ? "NEW SECTION" : "EDIT SECTION"}
				open={show}
				handleClose={() => handleCancel()}
				maxWidth="sm"
				id="form-section-modal"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content dividers>
						<Grid2 container spacing={2}>
							<Grid2 size={12}>
								<Controller
									name="SectionName"
									control={control}
									rules={{
										validate: (value) => value !== "",
									}}
									render={({ field: { onChange, value } }) => (
										<TextField
											label="Section Name"
											slotProps={{ inputLabel: { shrink: true } }}
											variant="outlined"
											fullWidth
											error={!!errors.SectionName}
											margin="dense"
											autoComplete="off"
											value={value}
											onChange={(e) => onChange(e)}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ md: 6, xs: 12 }}>
								<Controller
									name="BackgroundColor"
									control={control}
									render={({ field: { value, onChange } }) => (
										<ColorPicker
											label="Background Color"
											onChange={onChange}
											color={value as string}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ md: 6, xs: 12 }}>
								<Controller
									name="LayoutParameters"
									control={control}
									render={({ field: { value, onChange } }) => (
										<TextField
											select
											label="Layout"
											variant="outlined"
											fullWidth
											margin="dense"
											value={value ?? ""}
											onChange={(e) => onChange(e.target.value as string)}
										>
											<MenuItem value="Column=1">Column 1</MenuItem>
											<MenuItem value="Column=2">Column 2</MenuItem>
											<MenuItem value="Column=3">Column 3</MenuItem>
										</TextField>
									)}
								/>
							</Grid2>
							<Grid2 size={12}>
								<Controller
									name="Subtitle"
									control={control}
									render={({ field: { onChange, value } }) => (
										<TextField
											label="Subtitle"
											slotProps={{ inputLabel: { shrink: true } }}
											variant="outlined"
											fullWidth
											error={!!errors.Subtitle}
											margin="dense"
											autoComplete="off"
											value={value ?? ""}
											onChange={(e) => onChange(e)}
										/>
									)}
								/>
							</Grid2>
						</Grid2>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 16px 16px" }}>
						<Grid2 container spacing={2} justifyContent="flex-end" width="100%">
							<Grid2 size={{ md: 3, xs: 12 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={handleCancel}
								>
									Cancel
								</Button>
							</Grid2>
							<Grid2 size={{ md: 3, xs: 12 }}>
								<ButtonWithLoading
									fullWidth
									isLoading={isSubmitLoading}
									variant="contained"
									color="primary"
									type="submit"
								>
									Save
								</ButtonWithLoading>
							</Grid2>
						</Grid2>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<Snackbar
				open={editedSuccess}
				autoHideDuration={2500}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="info" onClose={handleClose}>
					Success
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
			<ErrorModal
				error={deleteByIdError}
				onHide={() => setDeleteByIdError("")}
			/>
		</>
	);
};
